<div class="container-fluid admin-page overview-page">
    <header class="admin-page-header">
        <div>
            <h1>{{ 'Overview' | translate }}</h1>
            <p>{{ 'Monitor fleet availability and order activity.' | translate }}</p>
        </div>
        <div class="admin-header-actions">
            <button (click)="refreshStatus()"
                    [disabled]="isLoadingOrderReports || isLoadingPaymentMethodReports"
                    class="admin-refresh-button" type="button">
                <i [class.spinning]="isLoadingOrderReports || isLoadingPaymentMethodReports"
                   class="material-icons">refresh</i>
                <span>{{ 'Refresh' | translate }}</span>
            </button>
        </div>
    </header>

    @if (!isNullOrUndefined(overviewData)) {
        <div class="metric-grid">
            <div class="metric-card">
                <span>{{ 'Total Cars' | translate }}</span>
                <strong>{{ overviewData.TotalCars }}</strong>
            </div>
            <div class="metric-card">
                <span>{{ 'Total Drivers' | translate }}</span>
                <strong>{{ overviewData.TotalDrivers }}</strong>
            </div>
            <div class="metric-card">
                <span>{{ 'Suspended Cars' | translate }}</span>
                <strong>{{ overviewData.SuspendedCars }}</strong>
            </div>
    </div>
    }

    @if (!isNullOrUndefined(onlineCars)) {
        <div class="metric-grid compact">
            <div class="metric-card">
                <span>{{ 'Online Cars' | translate }}</span>
                <strong>{{ onlineCars.OnlineCarsCount }}</strong>
            </div>
            <div class="metric-card">
                <span>{{ 'Available Cars' | translate }}</span>
                <strong>{{ onlineCars.OnlineCarsCount - onlineCars.BusyCarsCount }}</strong>
            </div>
    </div>
    }

    <section class="data-panel">
        <div class="panel-header">
            <div>
                <h2>{{ 'Total orders by platform' | translate }}</h2>
                <p>{{ 'Daily orders grouped by source for the last 9 days.' | translate }}</p>
            </div>
        </div>

        @if (isNullOrUndefined(totalOrderReports)) {
            <div class="loading-container">
                <div class="circle"></div>
            </div>
        }

        @if (!isNullOrUndefined(totalOrderReports) && hasPlatformOrders) {
            <div class="platform-chart-card">
                <div class="platform-legend">
                    @for (platform of platformLegend; track platform) {
                        <span>
              <i [style.backgroundColor]="platform.color"></i>{{ platform.label | translate }}
            </span>
                    }
                </div>
                <div class="platform-chart-scroll">
                    <div [attr.aria-label]="'Total orders by platform for the last nine days' | translate"
                         class="platform-chart" role="img">
                        <div aria-hidden="true" class="chart-grid-lines">
                            <i></i><i></i><i></i><i></i>
                        </div>
                        @for (day of totalOrderReports; track day) {
                            <div class="chart-day">
                                <div class="chart-bars">
                                    @for (bar of day.bars; track bar) {
                                        <div
                                                [attr.aria-label]="day.label + ', ' + (bar.label | translate) + ': ' + bar.value + ' ' + ('orders' | translate)"
                                                [attr.title]="day.label + ' · ' + (bar.label | translate) + ': ' + bar.value"
                                                class="chart-bar-slot">
                                            @if (bar.value > 0) {
                                                <span
                                                        [style.bottom.%]="bar.height"
                                                        class="chart-bar-value">{{ bar.value }}</span>
                                            }
                                            <i [style.backgroundColor]="bar.color"
                                               [style.height.%]="bar.height"
                                               class="chart-bar"></i>
                                        </div>
                                    }
                                </div>
                                <div class="chart-day-label">
                                    <strong>{{ day.label }}</strong>
                                    <span>{{ day.total }} {{ 'total' | translate }}</span>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>
        }

        @if (!isNullOrUndefined(totalOrderReports) && !hasPlatformOrders) {
            <div class="empty-state">
                <i class="material-icons">bar_chart</i>
                <span>{{ 'No orders found in the last 9 days.' | translate }}</span>
            </div>
        }
    </section>

    <section class="data-panel">
        <div class="panel-header">
            <div>
                <h2>{{ 'Today orders' | translate }}</h2>
                <p>{{ 'Orders grouped by platform for the current day.' | translate }}</p>
            </div>
        </div>

        @if (isNullOrUndefined(totalOrderReports)) {
            <div class="loading-container">
                <div class="circle"></div>
            </div>
        }

        @if (!isNullOrUndefined(totalOrderReports) && hasTodayOrders) {
            <div class="today-platform-chart">
                @for (bar of todayPlatformBars; track bar) {
                    <div
                            [attr.aria-label]="(bar.label | translate) + ': ' + bar.value + ' ' + ('orders today' | translate)"
                            class="today-chart-row">
            <span class="today-chart-label">
              <i [style.backgroundColor]="bar.color"></i>{{ bar.label | translate }}
            </span>
                        <div class="today-chart-measure">
                            <div class="today-chart-rail">
                                <i [style.backgroundColor]="bar.color"
                                   [style.width.%]="bar.width"
                                   class="today-chart-bar"></i>
                                <strong [style.left.%]="bar.width">{{ bar.value }}</strong>
                            </div>
                        </div>
                    </div>
                }
            </div>
        }

        @if (!isNullOrUndefined(totalOrderReports) && !hasTodayOrders) {
            <div class="empty-state">
                <i class="material-icons">receipt_long</i>
                <span>{{ 'No orders found for today.' | translate }}</span>
            </div>
        }
    </section>

    <section class="data-panel">
        <div class="panel-header">
            <div>
                <h2>{{ 'Orders by payment method' | translate }}</h2>
                <p>{{ 'Daily orders grouped by cash, card and voucher for the last 9 days.' | translate }}</p>
            </div>
        </div>

        @if (isNullOrUndefined(paymentMethodReports)) {
            <div class="loading-container">
                <div class="circle"></div>
            </div>
        }

        @if (!isNullOrUndefined(paymentMethodReports) && hasPaymentMethodOrders) {
            <div class="platform-chart-card">
                <div class="platform-legend">
                    @for (paymentMethod of paymentMethodLegend; track paymentMethod) {
                        <span>
              <i [style.backgroundColor]="paymentMethod.color"></i>{{ paymentMethod.label | translate }}
            </span>
                    }
                </div>
                <div class="platform-chart-scroll">
                    <div [attr.aria-label]="'Orders by payment method for the last nine days' | translate"
                         class="platform-chart" role="img">
                        <div aria-hidden="true" class="chart-grid-lines">
                            <i></i><i></i><i></i><i></i>
                        </div>
                        @for (day of paymentMethodReports; track day) {
                            <div class="chart-day">
                                <div class="chart-bars">
                                    @for (bar of day.bars; track bar) {
                                        <div
                                                [attr.aria-label]="day.label + ', ' + (bar.label | translate) + ': ' + bar.value + ' ' + ('orders' | translate)"
                                                [attr.title]="day.label + ' · ' + (bar.label | translate) + ': ' + bar.value"
                                                class="chart-bar-slot">
                                            @if (bar.value > 0) {
                                                <span
                                                        [style.bottom.%]="bar.height"
                                                        class="chart-bar-value">{{ bar.value }}</span>
                                            }
                                            <i [style.backgroundColor]="bar.color"
                                               [style.height.%]="bar.height"
                                               class="chart-bar"></i>
                                        </div>
                                    }
                                </div>
                                <div class="chart-day-label">
                                    <strong>{{ day.label }}</strong>
                                    <span>{{ day.total }} {{ 'total' | translate }}</span>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>
        }

        @if (!isNullOrUndefined(paymentMethodReports) && !hasPaymentMethodOrders) {
            <div class="empty-state">
                <i class="material-icons">payments</i>
                <span>{{ 'No payment data found in the last 9 days.' | translate }}</span>
            </div>
        }

        @if (!isNullOrUndefined(paymentMethodReports)) {
            <div
                    class="payment-report-table-wrap admin-data-table-wrap">
                <table class="payment-report-table admin-data-table">
                    <thead>
                    <tr>
                        <th>{{ 'Date' | translate }}</th>
                        <th>{{ 'Total' | translate }}</th>
                        <th>{{ 'Cash' | translate }}</th>
                        <th>{{ 'Card' | translate }}</th>
                        <th>{{ 'Voucher' | translate }}</th>
                    </tr>
                    </thead>
                    <tbody>
                        @for (day of paymentMethodReports; track day) {
                            <tr>
                                <td [attr.data-label]="'Date' | translate">{{ day.label }}</td>
                                <td [attr.data-label]="'Total' | translate">
                                    <strong>{{ day.total }} {{ 'orders' | translate }}
                                        ({{ formatAmount(day.totalAmount) }}
                                        )</strong>
                                </td>
                                <td [attr.data-label]="'Cash' | translate">
                                    {{ day.cash }} {{ 'orders' | translate }} ({{ formatAmount(day.cashAmount) }})
                                </td>
                                <td [attr.data-label]="'Card' | translate">
                                    {{ day.card }} {{ 'orders' | translate }} ({{ formatAmount(day.cardAmount) }})
                                </td>
                                <td [attr.data-label]="'Voucher' | translate">
                                    {{ day.voucher }} {{ 'orders' | translate }} ({{ formatAmount(day.voucherAmount) }})
                                </td>
                            </tr>
                        }
                    </tbody>
                </table>
            </div>
        }
    </section>

    <section class="data-panel">
        <div class="panel-header today-payment-header">
            <div>
                <h2>{{ 'Today orders by payment method' | translate }}</h2>
                <p>{{ 'Current-day orders grouped by payment method.' | translate }}</p>
            </div>
            @if (!isNullOrUndefined(paymentMethodReports)) {
                <div class="today-payment-summary">
                    <strong>{{ todayPaymentOrders }} {{ 'orders' | translate }} ({{ formatAmount(todayPaymentAmount) }}
                        )</strong>
                </div>
            }
        </div>

        @if (isNullOrUndefined(paymentMethodReports)) {
            <div class="loading-container">
                <div class="circle"></div>
            </div>
        }

        @if (!isNullOrUndefined(paymentMethodReports) && hasTodayPaymentMethodOrders) {
            <div
                    class="today-platform-chart">
                @for (bar of todayPaymentMethodBars; track bar) {
                    <div
                            [attr.aria-label]="(bar.label | translate) + ': ' + bar.value + ' ' + ('orders' | translate) + ', ' + formatAmount(bar.amount)"
                            class="today-chart-row">
            <span class="today-chart-label">
              <i [style.backgroundColor]="bar.color"></i>{{ bar.label | translate }}
            </span>
                        <div class="today-chart-measure">
                            <div class="today-chart-rail payment-method-rail">
                                <i [style.backgroundColor]="bar.color"
                                   [style.width.%]="bar.width"
                                   class="today-chart-bar">
                                    <strong class="today-payment-bar-value">
                                        {{ bar.value }} {{ 'orders' | translate }} / {{ formatAmount(bar.amount) }}
                                    </strong>
                                </i>
                            </div>
                        </div>
                    </div>
                }
            </div>
        }

        @if (!isNullOrUndefined(paymentMethodReports) && !hasTodayPaymentMethodOrders) {
            <div class="empty-state">
                <i class="material-icons">payments</i>
                <span>{{ 'No payment data found for today.' | translate }}</span>
            </div>
        }
    </section>
</div>
